

















import {
  computed,
  defineComponent,
  ref,
  watch,
  watchEffect,
} from "@vue/composition-api";
import IBAN, { printFormat } from "iban";

import { replaceStringWithCharUntil } from "../../state/helper/formData.helper";

import AsyncInputField from "@/shared/components/AsyncInputField";
import { useAsyncComponent } from "@/hooks/useAsyncComponent";
import { getBankData, BankData, cancelGetBankName } from "@/api/sap";

export default defineComponent({
  name: "SalesApplicationIBAN",
  components: {
    AsyncInputField,
  },
  setup(_, ctx) {
    const { isLoading, withLoading } = useAsyncComponent();

    const validationRules = computed(() =>
      ctx.attrs.disabled ? "required|max:34" : "required|iban|max:34"
    );

    const ibanModel = ref(ctx.attrs.value || "");
    const iban = computed({
      get: () => {
        let value = ibanModel.value;

        if (ctx.attrs.hideIban) {
          value = replaceStringWithCharUntil(value, "X", 6);
        }

        return printFormat(value, " ");
      },
      set: (val) => {
        ibanModel.value = val.replace(/\s/g, "").toUpperCase().slice(0, 22);
      },
    });
    const bankData = ref<BankData | Record<string, unknown>>({});
    const customErrors = ref<string[]>([]);

    // Request Bank Data if initialized with iban
    ctx.attrs.value && requestBankData();
    watch(iban, async (iban, prevIban) => {
      if (iban === prevIban) {
        return;
      }

      requestBankData();
    });

    watchEffect(() => syncLoading());

    async function requestBankData() {
      cancelBankDataRequest();
      resetBankData();
      resetErrors();

      if (!ctx.attrs.disabled && !IBAN.isValid(iban.value)) {
        return;
      }

      try {
        const response = ctx.attrs.disabled
          ? await withLoading(getBankData(ibanModel.value))
          : await withLoading(getBankData(iban.value));

        if (response.errorMsg) {
          customErrors.value = [response.errorMsg, ...customErrors.value];
        } else {
          bankData.value = response.bankData;
        }
      } catch (err) {
        if (err.message !== "cancelBankDataRequest") {
          customErrors.value = [err.userMessage, ...customErrors.value];
        }
      } finally {
        syncBankData();
        syncErrors();
      }
    }

    function cancelBankDataRequest() {
      if (isLoading.value) {
        cancelGetBankName("cancelBankDataRequest");
      }
    }

    function emitIBAN() {
      ctx.emit("input", ibanModel.value);
    }

    function resetBankData() {
      bankData.value = {};
      syncBankData();
    }

    function resetErrors() {
      customErrors.value = [];
    }

    function syncBankData() {
      ctx.emit("update:bankName", bankData.value.bankName);
      ctx.emit("update:swift", bankData.value.swift);
      ctx.emit("update:bankCode", bankData.value.bankCode);
    }

    function syncErrors() {
      ctx.emit("update:errors", customErrors.value);
    }

    function syncLoading() {
      ctx.emit("update:loading", isLoading.value);
    }

    return {
      validationRules,
      customErrors,
      requestBankData,
      iban,
      emitIBAN,
      isLoading,
    };
  },
});
